import React, { useEffect, useMemo, useState } from "react";
import ChildHeaderCard from "./components/ChildHeaderCard";
import PrivacyChannelList from "./components/PrivacyChannelList";
import PrivacyVisibilityCard from "./components/PrivacyVisibilityCard";
import StickyActionBar from "./components/StickyActionBar";
import { getChildKey } from "./utils/childUtils";
import {
  PRIVACY_CHANNELS,
  buildInitialChannels,
  normalizePrivacyVisibility,
} from "../../../features/privacy/definitions";

const ChildPrivacyEdit = ({
  t,
  child,
  classroomsByChild = {},
  privacyByChild,
  savePrivacyFor,
  pendingChildren = [],
  pendingChildUpdateSet = new Set(),
  onBack,
  onSaved,
}) => {
  const key = getChildKey(child);
  const privacy = privacyByChild?.[key]?.data;
  const hasExistingPrivacy = !!privacy && typeof privacy === "object";
  const originalVisibility = normalizePrivacyVisibility(privacy?.visibility);

  const [channels, setChannels] = useState(() => buildInitialChannels(privacy));
  const [visibility, setVisibility] = useState(originalVisibility);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setChannels(buildInitialChannels(privacy));
    setVisibility(normalizePrivacyVisibility(privacy?.visibility));
  }, [privacy]);

  const hasChanges = useMemo(() => {
    if (!hasExistingPrivacy) return true;

    const channelChanged = PRIVACY_CHANNELS.some(
      ({ key: channelKey }) =>
        !!channels[channelKey] !== !!privacy?.[channelKey],
    );

    return channelChanged || visibility !== originalVisibility;
  }, [channels, hasExistingPrivacy, originalVisibility, privacy, visibility]);

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      const payload = PRIVACY_CHANNELS.reduce(
        (acc, { key: channelKey }) => ({
          ...acc,
          [channelKey]: !!channels[channelKey],
        }),
        {},
      );

      await savePrivacyFor?.(child.id, {
        ...payload,
        visibility: visibility === "UNSET" ? null : visibility,
        ...(privacy?.id ? { id: privacy.id } : {}),
      });

      onSaved?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("privacy.save_error", { defaultValue: "Failed to save changes" }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pp-family-view mx-auto grid w-full max-w-[33rem] gap-3 pb-[7.25rem]">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-1 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-fit items-center gap-2 pt-3 text-[0.92rem] font-black text-emerald-700"
        >
          <span className="text-xl leading-none" aria-hidden="true">
            ‹
          </span>
          {t("common.back", { defaultValue: "Back" })}
        </button>

        <ChildHeaderCard
          t={t}
          child={child}
          classroomsByChild={classroomsByChild}
          pendingChildren={pendingChildren}
          pendingChildUpdateSet={pendingChildUpdateSet}
        />
      </div>

      <div className="px-1 pt-2">
        <h2 className="m-0 text-[1.45rem] font-black leading-tight text-slate-950 dark:text-slate-50">
          {t("privacy.title", { defaultValue: "Privacy & publication" })}
        </h2>
        <p className="mt-1.5 text-[0.92rem] font-semibold leading-snug text-slate-600 dark:text-slate-300">
          {t("privacy.choose_channels", {
            defaultValue: "Choose what may be shared.",
          })}
        </p>
      </div>

      <PrivacyChannelList
        t={t}
        channels={channels}
        setChannels={setChannels}
        visibilitySlot={
          <PrivacyVisibilityCard
            t={t}
            childKey={key}
            visibility={visibility}
            setVisibility={setVisibility}
          />
        }
      />

      {error && (
        <p className="m-0 rounded-xl bg-red-50 px-4 py-3 text-[0.85rem] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      <StickyActionBar
        t={t}
        saving={saving}
        disabled={!hasChanges}
        onCancel={onBack}
        onSave={handleSave}
      />
    </div>
  );
};

export default ChildPrivacyEdit;
